import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api, tokenStorage } from '../api/client';

const ShopContext = createContext(null);

export function ShopProvider({ children }) {
  const [shops, setShops] = useState([]);
  const [currentShopId, setCurrentShopId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadShops = useCallback(() => {
    // Don't fetch if no auth token yet
    if (!tokenStorage.getAccess()) return;
    setLoading(true);
    api.getShops()
      .then((data) => {
        setShops(data.shops || []);
        if (data.current_shop_id) setCurrentShopId(data.current_shop_id);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadShops();
    // Re-load on auth token change (login/logout)
    const handler = () => loadShops();
    window.addEventListener('storage', handler);
    window.addEventListener('auth-changed', handler);
    return () => {
      window.removeEventListener('storage', handler);
      window.removeEventListener('auth-changed', handler);
    };
  }, [loadShops]);

  const switchShop = useCallback(async (shopId) => {
    // Client-side only — the frontend now sends shop_id on every audit request
    setCurrentShopId(shopId);
  }, []);

  const currentShop = shops.find(
    (s) => s.id === currentShopId || s.shop_id === currentShopId
  );

  return (
    <ShopContext.Provider
      value={{
        shops,
        currentShopId,
        currentShop,
        switchShop,
        refreshShops: loadShops,
        loading,
        error,
      }}
    >
      {children}
    </ShopContext.Provider>
  );
}

export function useShop() {
  const ctx = useContext(ShopContext);
  if (!ctx) throw new Error('useShop must be used within ShopProvider');
  return ctx;
}
